import type { AdminV2ToolDisplayIcon } from './tool-icon';
import type { AdminV2ToolRuntimePackage } from './tool-types';

type ToolDisplay = Omit<AdminV2ToolRuntimePackage['display'], 'icon'> & {
  icon: AdminV2ToolDisplayIcon | null;
};

export class AdminV2ToolLocalizedCopyError extends Error {}

function readRequiredLocalizedText(params: {
  value: Record<string, string | undefined> | undefined;
  locale: string;
  fieldPath: string;
}) {
  const text = params.value?.[params.locale]?.trim();
  if (!text) {
    throw new AdminV2ToolLocalizedCopyError(
      `Admin v2 published Tool runtime is missing localized text at ${params.fieldPath}.${params.locale}.`
    );
  }
  return text;
}

function resolveOptionalDisplayText(params: {
  sourceText: string | null | undefined;
  localized: Record<string, string | undefined> | undefined;
  locale: string;
  fieldPath: string;
}) {
  const sourceText = params.sourceText?.trim() || null;
  if (params.locale === 'en') return sourceText;
  if (!sourceText) return null;
  return readRequiredLocalizedText({
    value: params.localized,
    locale: params.locale,
    fieldPath: params.fieldPath,
  });
}

export function resolveAdminV2ToolDisplay(params: {
  display: ToolDisplay;
  locale: string;
  fieldPath: string;
}) {
  const name =
    params.locale === 'en'
      ? params.display.name.trim()
      : readRequiredLocalizedText({
          value: params.display.localized?.name,
          locale: params.locale,
          fieldPath: `${params.fieldPath}.name`,
        });
  if (!name) {
    throw new AdminV2ToolLocalizedCopyError(
      `Admin v2 published Tool runtime is missing source text at ${params.fieldPath}.name.en.`
    );
  }

  return {
    name,
    badge: resolveOptionalDisplayText({
      sourceText: params.display.badge,
      localized: params.display.localized?.badge,
      locale: params.locale,
      fieldPath: `${params.fieldPath}.badge`,
    }),
    icon:
      typeof params.display.icon === 'string'
        ? params.display.icon.trim() || null
        : (params.display.icon ?? null),
    summary: resolveOptionalDisplayText({
      sourceText: params.display.summary,
      localized: params.display.localized?.summary,
      locale: params.locale,
      fieldPath: `${params.fieldPath}.summary`,
    }),
  };
}

function assertOptionalLocalizedText(params: {
  localized: Record<string, string | undefined> | undefined;
  locale: string;
  fieldPath: string;
}) {
  if (!params.localized) return;
  readRequiredLocalizedText({
    value: params.localized,
    locale: 'en',
    fieldPath: params.fieldPath,
  });
  if (params.locale === 'en') return;
  readRequiredLocalizedText({
    value: params.localized,
    locale: params.locale,
    fieldPath: params.fieldPath,
  });
}

function assertRuntimeLocalizedCopy(params: {
  runtimePackage: AdminV2ToolRuntimePackage;
  locale: string;
}) {
  resolveAdminV2ToolDisplay({
    display: params.runtimePackage.display,
    locale: params.locale,
    fieldPath: `tools.${params.runtimePackage.toolId}.display`,
  });

  for (const field of params.runtimePackage.toolFields) {
    readRequiredLocalizedText({
      value: field.label,
      locale: params.locale,
      fieldPath: `tools.${params.runtimePackage.toolId}.fields.${field.id}.label`,
    });
    assertOptionalLocalizedText({
      localized: field.placeholder,
      locale: params.locale,
      fieldPath: `tools.${params.runtimePackage.toolId}.fields.${field.id}.placeholder`,
    });
    assertOptionalLocalizedText({
      localized: field.tooltip,
      locale: params.locale,
      fieldPath: `tools.${params.runtimePackage.toolId}.fields.${field.id}.tooltip`,
    });
    for (const option of field.options ?? []) {
      readRequiredLocalizedText({
        value: option.label,
        locale: params.locale,
        fieldPath: `tools.${params.runtimePackage.toolId}.fields.${field.id}.options.${String(option.value)}.label`,
      });
      assertOptionalLocalizedText({
        localized: option.description,
        locale: params.locale,
        fieldPath: `tools.${params.runtimePackage.toolId}.fields.${field.id}.options.${String(option.value)}.description`,
      });
    }
  }

  const inputLabels = params.runtimePackage.workspaceView.inputLabels;
  assertOptionalLocalizedText({
    localized: inputLabels?.primaryImage,
    locale: params.locale,
    fieldPath: `tools.${params.runtimePackage.toolId}.workspaceView.inputLabels.primaryImage`,
  });
  assertOptionalLocalizedText({
    localized: inputLabels?.referenceImage,
    locale: params.locale,
    fieldPath: `tools.${params.runtimePackage.toolId}.workspaceView.inputLabels.referenceImage`,
  });
  assertOptionalLocalizedText({
    localized: inputLabels?.frameMode,
    locale: params.locale,
    fieldPath: `tools.${params.runtimePackage.toolId}.workspaceView.inputLabels.frameMode`,
  });
  for (const binding of params.runtimePackage.workspaceView.actionBindings ??
    []) {
    if (!binding.confirmation) continue;
    for (const key of [
      'title',
      'description',
      'confirmLabel',
      'cancelLabel',
    ] as const) {
      assertOptionalLocalizedText({
        localized: binding.confirmation[key],
        locale: params.locale,
        fieldPath: `tools.${params.runtimePackage.toolId}.workspaceView.actionBindings.${binding.slotId}.confirmation.${key}`,
      });
    }
  }
  for (const calculator of params.runtimePackage.workspaceView
    .parameterCalculators ?? []) {
    for (const item of calculator.copy) {
      readRequiredLocalizedText({
        value: item.text,
        locale: params.locale,
        fieldPath: `tools.${params.runtimePackage.toolId}.workspaceView.parameterCalculators.${calculator.id}.copy.${item.id}`,
      });
    }
  }
}
